/**
 * 부서별 월간보고서 — 보기·결재 모달 (v2.280.0 · 2026-10-19)
 *
 * 경영지원부·기업부설연구소가 사내결재 문서함에서 쓴 월간보고서. 본문(전월·당월 표)은 사람이 적은 그대로 보여 주고,
 * 결재(승인·반려·회수·삭제)는 지출결재와 **같은 API** — 여기서 다시 만들지 않는다.
 *
 * ⭐고치기는 **작성자 본인의 작성중·반려 문서**만 — [✏ 수정]이 작성 창(DeptMonthlyReportFormModal)을 이 창 **안에** 띄운다
 *   (다른 모달 위에 띄우려면 그 모달 안에 그려야 한다 — 자기 z 는 부모 안에서만 셈). 서버도 같은 조건으로만 PUT 을 받는다.
 * ⭐보기 창이라 ESC 로 닫힌다(useDialogLayer) — 위에 작성 창이 열리면 그쪽(입력 층)이 ESC 를 삼킨다.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/expense-api';
import { deptPeriodText, nextPeriodYm, type DeptReportBody, type DeptReportRow } from '../lib/dept-report-api';
import { EXPENSE_STATUS_BADGE } from './ExpenseDocModal';
import { signatureImageUrl } from '../lib/approval-api';
import { DocHistoryList } from './DocHistoryList';
import { openServerPdfPreview } from '../lib/pdf-preview';
import { useAuth } from '../lib/auth-context';
import { useDialogLayer } from '../lib/dialog-stack';
import { DeptMonthlyReportFormModal } from './DeptMonthlyReportFormModal';
import { LoadingLines } from './ui/Skeleton';

interface Props {
  docId: number;
  onClose: () => void;
}

/**
 * 양식 표 그대로 — 구분 · 【키워드】 내용 · 비고. **같은 구분이 이어지면 세로 병합**(PDF 와 같은 규칙).
 * 작성 창의 미리보기와 보기 창이 같이 쓴다.
 */
export function DeptReportRowsTable({ rows }: { rows: DeptReportRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="text-[13px] text-slate-400 border border-slate-200 rounded px-3 py-3 text-center">
        적힌 내용이 없습니다
      </div>
    );
  }
  const spans: number[] = rows.map(() => 0);
  for (let i = 0; i < rows.length;) {
    let j = i + 1;
    while (j < rows.length && rows[j]!.category === rows[i]!.category) j += 1;
    spans[i] = j - i;
    i = j;
  }
  return (
    <div className="border border-slate-200 rounded overflow-hidden">
      <table className="w-full text-[13px] table-fixed">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="px-2 py-1.5 font-medium w-[88px] text-center">구분</th>
            <th className="px-3 py-1.5 font-medium text-left">내용</th>
            <th className="px-2 py-1.5 font-medium w-[120px] text-left">비고</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100 align-top">
              {spans[i]! > 0 && (
                <td rowSpan={spans[i]} className="px-2 py-1.5 text-center font-medium text-slate-700 bg-slate-50/60 border-r border-slate-100 whitespace-pre-line">
                  {r.category}
                </td>
              )}
              <td className="px-3 py-1.5 whitespace-pre-wrap break-words">
                {r.keyword && <b className="text-slate-900 mr-1">【 {r.keyword} 】</b>}
                {r.content}
              </td>
              <td className="px-2 py-1.5 text-slate-600 whitespace-pre-wrap break-words">{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DeptMonthlyReportModal({ docId, onClose }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  // 보기 창 — ESC 로 닫힌다 (작성 창이 위에 열리면 그쪽 입력 층이 먼저 받는다)
  useDialogLayer(true, onClose);

  const detail = useQuery({
    queryKey: ['expense', 'doc', docId],
    queryFn: () => api.getExpenseDoc(docId),
  });

  const doc = detail.data?.doc ?? null;
  const body: DeptReportBody | null = detail.data?.deptReport ?? null;

  // 작성자 본인 + 작성중·반려일 때만 고칠 수 있다 (서버도 같은 조건)
  const canEdit =
    !!doc && !!user && doc.authorId === user.userId
    && (doc.status === '작성중' || doc.status === '반려');

  const act = useMutation({
    mutationFn: (kind: 'approve' | 'reject' | 'recall') => {
      if (kind === 'approve') return api.approveExpenseDoc(docId);
      if (kind === 'recall') {
        // ⭐완결 문서 회수는 사유 필수 (v2.101.0) — 다른 결재 모달과 같은 규칙
        if (doc?.status === '최종승인') {
          const r = window.prompt('완결(최종승인) 문서를 회수합니다.\n사유를 적어 주세요 (필수 — 처리 이력에 남습니다)') ?? '';
          if (!r.trim()) return Promise.reject(new Error('사유가 없으면 완결 문서를 회수할 수 없습니다'));
          return api.recallExpenseDoc(docId, r.trim());
        }
        return api.recallExpenseDoc(docId);
      }
      const reason = window.prompt('반려 사유를 적어 주세요 (선택)\n\n※ 작성자가 내용을 고쳐 다시 상신합니다.') ?? '';
      return api.rejectExpenseDoc(docId, reason);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense'] });
      onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  const del = useMutation({
    mutationFn: () => api.deleteExpenseDoc(docId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense'] });
      onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  const busy = act.isPending || del.isPending;
  const title = body ? `${body.dept} 월간보고서` : '부서별 월간보고서';

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-4xl my-6">
        {/* 머리 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <h2 className="ui-modal-title shrink-0">{title}</h2>
            {doc && <span className="text-xs text-slate-500 font-mono shrink-0">{doc.docNo}</span>}
            {doc && (
              <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${EXPENSE_STATUS_BADGE[doc.status] ?? ''}`}>
                {doc.status}
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} className="ui-close">✕</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {detail.isLoading && <div className="text-sm text-slate-400 py-6 text-center"><LoadingLines /></div>}

          {doc?.rejectReason && (
            <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-3 py-2">
              반려 사유: {doc.rejectReason}
            </div>
          )}

          {/* 1. 월간 업무 개요 */}
          {body && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">1. 월간 업무 개요</div>
              <table className="w-full text-[13px] border border-slate-200 rounded overflow-hidden">
                <tbody>
                  <tr className="border-b border-slate-100">
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2 w-[110px]">대상 월</th>
                    <td className="px-3 py-2">
                      <b>{deptPeriodText(body.periodYm)}</b>
                      <span className="text-slate-500 text-xs ml-1.5">({body.periodYm})</span>
                    </td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2">소속 · 직급 · 성명</th>
                    <td className="px-3 py-2">
                      {body.author.dept || body.dept}
                      <span className="text-slate-500 mx-1.5">·</span>{body.author.rank || '—'}
                      <span className="text-slate-500 mx-1.5">·</span><b>{body.author.name || doc?.authorName}</b>
                    </td>
                  </tr>
                  <tr>
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2">작성 · 수정</th>
                    <td className="px-3 py-2 text-slate-600 text-[13px]">
                      {doc?.docDate ?? '—'}
                      {body.updatedAt && body.createdAt && body.updatedAt !== body.createdAt && (
                        <span className="text-slate-400 ml-1.5">· 수정 {body.updatedAt.slice(0, 16)}</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>
          )}

          {/* 2. 전월 · 3. 당월 */}
          {body && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">
                2. 전월 <span className="font-normal text-slate-500">— {deptPeriodText(body.periodYm)} 업무</span>
              </div>
              <DeptReportRowsTable rows={body.prevRows} />
            </section>
          )}
          {body && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">
                3. 당월 <span className="font-normal text-slate-500">— {deptPeriodText(nextPeriodYm(body.periodYm))} 업무 계획</span>
              </div>
              <DeptReportRowsTable rows={body.curRows} />
            </section>
          )}

          {/*
            결재판 — ⭐이 문서는 **3칸**이다(담당 → 부서장 → 대표이사 · 경영지원 없음).
            칸 수를 적지 않고 서버가 준 cells 를 그대로 훑는다(4칸으로 못박으면 종류가 늘 때마다 어긋난다).
          */}
          {detail.data && detail.data.cells.length > 0 && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">
                결재 <span className="font-normal text-slate-400">— 경영지원 결재는 없습니다</span>
              </div>
              <div className="flex gap-2">
                {detail.data.cells.map((c) => (
                  <div key={c.role} className="border border-slate-300 rounded text-center w-[86px]">
                    <div className="text-[11px] bg-slate-100 py-0.5 text-slate-600">{c.role}</div>
                    <div className="h-9 flex items-center justify-center text-xs">
                      {c.skipped ? (
                        <span className="text-slate-300">생략</span>
                      ) : c.stamped ? (
                        c.userId && c.hasSignature ? (
                          <img src={signatureImageUrl(c.userId)} alt={c.name}
                            className="max-h-[30px] max-w-[70px] object-contain"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        ) : (
                          <span className="font-semibold text-slate-700">{c.name}</span>
                        )
                      ) : (
                        <span className="text-slate-300">대기</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 pb-0.5">
                      {c.skipped ? '' : (c.stampedAt ?? '').slice(2, 10)}
                      {!c.skipped && c.delegated && (
                        <span className="ml-0.5 text-[11px] text-amber-600 font-semibold">(대결)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <DocHistoryList history={detail.data?.history} />
        </div>

        {/* 단추 */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3">
          <div className="flex gap-2 items-center">
            {detail.data?.attachmentId ? (
              <button type="button"
                onClick={() => openServerPdfPreview({
                  pdfUrl: api.attachmentDownloadUrl(detail.data!.attachmentId!),
                  filename: `${doc?.docNo ?? '월간보고서'}_${(body?.dept ?? '부서')}월간보고서.pdf`,
                })}
                className="btn btn-secondary">
                📄 보고서 보기
              </button>
            ) : (
              // 상신 전(작성중)에는 확정본이 아직 없다 — 없는 이유를 적어 둔다
              <span className="text-[12px] text-slate-400">확정본 PDF 는 상신할 때 만들어집니다.</span>
            )}
            {canEdit && body && (
              <button type="button" disabled={busy} onClick={() => setEditing(true)} className="btn btn-primary">
                ✏ 수정{doc?.status === '반려' ? ' · 다시 상신' : ''}
              </button>
            )}
            {detail.data?.canDelete && (
              <button type="button" disabled={busy}
                onClick={() => {
                  if (window.confirm(`${doc?.docNo ?? '이 문서'} 를 삭제할까요?\n그 달 보고서를 다시 올릴 수 있게 됩니다.`)) del.mutate();
                }}
                className="btn btn-danger">
                {del.isPending ? '삭제 중…' : '🗑 삭제'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {detail.data?.canRecall && (
              <button type="button" onClick={() => act.mutate('recall')} disabled={busy}
                className="btn btn-secondary">↩ 승인 회수</button>
            )}
            {detail.data?.canReject && (
              <button type="button" onClick={() => act.mutate('reject')} disabled={busy}
                className="btn btn-danger">반려</button>
            )}
            {detail.data?.canApprove && (
              <button type="button" onClick={() => act.mutate('approve')} disabled={busy}
                className="btn btn-success-solid">승인</button>
            )}
          </div>
        </div>

        {/* 작성 창 — 이 창 안에 그려야 위에 뜬다. 저장하면 상세를 다시 읽고, 상신까지 했으면 이 창도 닫는다 */}
        {editing && body && (
          <DeptMonthlyReportFormModal
            mode="edit"
            docId={docId}
            dept={body.dept}
            initialPeriodYm={body.periodYm}
            initialPrevRows={body.prevRows}
            initialCurRows={body.curRows}
            author={body.author}
            onClose={() => setEditing(false)}
            onSaved={(submitted) => {
              setEditing(false);
              qc.invalidateQueries({ queryKey: ['expense'] });
              if (submitted) onClose();
            }}
          />
        )}
      </div>
    </div>
  );
}
